import React from 'react';
import { isNil } from 'lodash';
import TableColumnTitle from '../../../../../components/_table/TableColumnTitle';
import TableFilterInput from '../../../../../components/_table/TableFilterInput';
import Anchor from '../../../../../components/link/Anchor';
import { openNewWindow, setDash, setDetailPageUrl, setNumberWithComma } from '../../../../../lib/common';
import { BRAND_DETAIL_URL_PATH, PRODUCT_DETAIL_URL_PATH } from '../../../../../constants/service/common/urlConstants';
import { AntdColumn, AntdColumnUtilType } from '../../../../../types/common/table';
import { MainCategoryItem, PRODUCT_STATUS_V2, ProductItemV2 } from '../../../../../types/product';
import { DateRageType } from '../../../../../types/common/date';
import moment from '../../../../../lib/moment';
import { DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import { WidgetProductFilterOption } from '../../../../../types/widget';
import TableColumnModalImg from '../../../../../components/table/TableColumModalImg';
import { MainCategoryItemV2 } from '../../../../../types/category';
import MainCategorySelect from './MainCategorySelect';
import MainCategoryName from './MainCategoryName';
import { PRODUCT_STATUS_LABEL_V2 } from '../../../../../constants/service/app/productConstantsV2';

export const productColumns = (
  data: AntdColumnUtilType<ProductItemV2, WidgetProductFilterOption>,
): AntdColumn<ProductItemV2>[] => {
  const { filterOption, onFilter, sortOption } = data;

  return [
    {
      title: <TableColumnTitle title={'이미지'} />,
      dataIndex: 'imageUrl',
      width: 100,
      render: (value) => <TableColumnModalImg url={value} />,
    },
    {
      title: <TableColumnTitle title={'브랜드명'} filterValue={filterOption.brandName} />,
      dataIndex: 'brandName',
      filterable: true,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <TableFilterInput
          placeholder={'브랜드명 검색'}
          option={filterOption.brandName}
          confirm={confirm}
          onFilter={(brandName: string): void => onFilter({ brandName })}
        />
      ),
    },
    {
      title: <TableColumnTitle title={'브랜드 ID'} filterValue={filterOption.brandId} />,
      dataIndex: 'brandId',
      filterable: true,
      width: 70,
      render: (value: number) => (
        <Anchor
          color="primary"
          isUnderLine
          text={value}
          onClick={(): void => openNewWindow(setDetailPageUrl(BRAND_DETAIL_URL_PATH, value))}
        />
      ),
      filterDropdown: ({ confirm }) => (
        <TableFilterInput
          type="number"
          placeholder={'브랜드 ID 검색'}
          option={filterOption.brandId}
          confirm={confirm}
          onFilter={(brandId: string): void => onFilter({ brandId: isNil(brandId) ? brandId : Number(brandId) })}
        />
      ),
    },
    {
      title: <TableColumnTitle title={'상품 번호'} filterValue={filterOption.id} />,
      dataIndex: 'id',
      filterable: true,
      render: (value: number) => (
        <Anchor
          color="primary"
          isUnderLine
          text={value}
          onClick={(): void => openNewWindow(setDetailPageUrl(PRODUCT_DETAIL_URL_PATH, value))}
        />
      ),
      filterDropdown: ({ confirm }) => (
        <TableFilterInput
          type="number"
          placeholder={'상품 번호 검색'}
          option={filterOption.id}
          confirm={confirm}
          onFilter={(id: string): void => onFilter({ id: isNil(id) ? id : Number(id) })}
        />
      ),
    },
    {
      title: (
        <TableColumnTitle
          title={'1차 카테고리'}
          filterValue={
            filterOption.mainCategoryList && <MainCategoryName mainCategoryId={filterOption.mainCategoryList} />
          }
        />
      ),
      dataIndex: 'mainCategoryList',
      width: 500,
      filterable: true,
      filterDropdown: ({ confirm }) => (
        <MainCategorySelect
          value={filterOption.mainCategoryList || null}
          onChange={(mainCategoryId: number | null): void => {
            onFilter({ mainCategoryList: mainCategoryId });
            confirm();
          }}
        />
      ),
      render: (value: MainCategoryItem[]) => (
        <div>{value?.map((category: MainCategoryItemV2) => category.name).join(', ')}</div>
      ),
    },
    {
      title: <TableColumnTitle title={'상품명'} filterValue={filterOption.name} />,
      dataIndex: 'name',
      className: 'align-left',
      resizable: true,
      filterable: true,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <TableFilterInput
          placeholder={'상품명 검색'}
          option={filterOption.name}
          confirm={confirm}
          onFilter={(name: string): void => onFilter({ name })}
        />
      ),
    },
    {
      title: <TableColumnTitle title={'상태'} />,
      dataIndex: 'status',
      width: 100,
      render: (value: PRODUCT_STATUS_V2) => <>{setDash(PRODUCT_STATUS_LABEL_V2[value])}</>,
    },
    {
      title: <TableColumnTitle title={'판매가 (원)'} />,
      dataIndex: 'originPrice',
      sorter: true,
      sortOrder: sortOption['originPrice'],
      render: (value: number) => setNumberWithComma(value),
    },
    {
      title: <TableColumnTitle title={'할인가 (원)'} />,
      dataIndex: 'discountPrice',
      sorter: true,
      sortOrder: sortOption['discountPrice'],
      render: (value: number) => setNumberWithComma(value),
    },
    {
      title: <TableColumnTitle title={'할인율 (%)'} />,
      dataIndex: 'discountRate',
      sorter: true,
      sortOrder: sortOption['discountRate'],
      render: (value: number) => setNumberWithComma(value),
    },
    {
      title: <TableColumnTitle title={'할인기간'} />,
      dataIndex: 'discountPeriod',
      render: (period?: DateRageType) => (
        <>
          {period?.startDate && period?.endDate
            ? `${moment(period.startDate).format(DATE_FORMAT.FULL_DASH_FORMAT)} ~ ${moment(period.endDate).format(
                DATE_FORMAT.FULL_DASH_FORMAT,
              )}`
            : '-'}
        </>
      ),
    },
    {
      title: <TableColumnTitle title={'실판매가 (원)'} />,
      dataIndex: 'salesPrice',
      sorter: true,
      sortOrder: sortOption['salesPrice'],
      render: (value: number) => setNumberWithComma(value),
    },
    {
      title: <TableColumnTitle title={'등록일'} />,
      dataIndex: 'createDate',
      sorter: true,
      sortOrder: sortOption['createDate'],
      width: 90,
      render: (date: string) => <>{moment(date).format(DATE_FORMAT.YMD_DASH_FORMAT)}</>,
    },
  ];
};
